import { useCurrentUser } from "#/components/context/user/UserContext"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
} from "#/components/cmd-enrollment/shared"
import { Steps } from "#/components/cmd-enrollment/workflow/steps"
import React, { useEffect } from "react"
import {
  FieldNameHealthPlanId,
  FieldNameTradingPartner,
  UseConnectHealthPlan,
  useConnectHealthPlan,
  UseTradingPartners,
  useTradingPartners,
  providerNotListedPk
} from "./hooks"
import { useUserDeductible } from "../../../../context/user/DeductibleContext"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { FormTextField, Form, useFormContext, useWatch } from "@carrotfertility/carotene-core-x"

export interface ProviderConnectYourInsuranceProps {
  viewName: typeof Steps[keyof typeof Steps]
  defaultTradingPartnerPk: string | null
  defaultHealthPlanId: string
  onUserUpdate: (healthPlanIdVal: string, tradingPartnerPkVal: string, tradingPartnerId: string) => any
  stepHeader: string
}

function SubmitButton({ onSubmit }: { onSubmit: UseConnectHealthPlan["onSubmit"] }): JSX.Element {
  const { handleSubmit } = useFormContext()
  return <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
}

function FormContent({
  defaultHealthPlanId,
  stepHeader,
  options,
  defaultTradingPartnerPk,
  onSubmit
}: {
  defaultHealthPlanId: string
  stepHeader: string
  options: UseTradingPartners["options"]
  defaultTradingPartnerPk: string | null
  onSubmit: UseConnectHealthPlan["onSubmit"]
}): JSX.Element {
  const intl = useIntl()
  const theme = useTheme()
  const { setValue } = useFormContext()
  const selectedTradingPartnerPk = useWatch({ name: FieldNameTradingPartner })
  const providerListed = Number(selectedTradingPartnerPk) !== providerNotListedPk

  useEffect(() => {
    setValue(FieldNameHealthPlanId, defaultHealthPlanId)
  }, [defaultHealthPlanId, setValue])

  return (
    <>
      <Box marginBottom={(theme) => theme.tokens.spacing.xxxl}>
        <BenefitEnrollmentFlowModalBackButton />
      </Box>
      <Typography
        id="step-heading"
        variant="h2"
        color={(theme) => theme.palette.text.primary}
        paddingBottom={(theme) => theme.tokens.spacing.xl}
      >
        {stepHeader}
      </Typography>
      {/* Stack spaces only the fields that render, so hiding Member ID leaves no stray gap. */}
      <Stack
        spacing={theme.spacing(theme.tokens.spacing.lg)}
        marginBottom={(theme) => (providerListed ? theme.tokens.spacing.xl : theme.tokens.spacing.xxxl)}
      >
        <Box>
          <Typography
            id="provider-name-label"
            variant="body2"
            color={(theme) => theme.palette.text.primary}
            marginBottom={(theme) => theme.tokens.spacing.xs}
            display="block"
            fontWeight="bold"
            textTransform="uppercase"
          >
            <FormattedMessage defaultMessage="PROVIDER NAME" />
          </Typography>
          <BenefitEnrollmentModalFormSelect
            name={FieldNameTradingPartner}
            aria-labelledby="provider-name-label"
            defaultValue={defaultTradingPartnerPk}
            options={options}
          />
        </Box>
        {providerListed && (
          <FormTextField
            name={FieldNameHealthPlanId}
            label={intl.formatMessage({ defaultMessage: "Member ID" })}
            defaultValue={defaultHealthPlanId ?? ""}
            registerOptions={{
              required: intl.formatMessage({ defaultMessage: "Required" }),
              pattern: {
                value: /^[a-zA-Z0-9]+$/,
                message: intl.formatMessage({ defaultMessage: "Only numbers and letters may be entered." })
              }
            }}
          />
        )}
      </Stack>
      <Box height={(theme) => theme.spacing(theme.tokens.spacing.xxl)} />
      <SubmitButton onSubmit={onSubmit} />
    </>
  )
}

function ProviderConnectYourInsurance({
  viewName,
  defaultTradingPartnerPk,
  defaultHealthPlanId,
  onUserUpdate,
  stepHeader
}: ProviderConnectYourInsuranceProps): JSX.Element {
  const { options, availableTradingPartners } = useTradingPartners()
  const { onSubmit } = useConnectHealthPlan({ viewName, onUserUpdate, availableTradingPartners })

  return (
    <Form onSubmit={onSubmit}>
      <FormContent
        defaultHealthPlanId={defaultHealthPlanId}
        stepHeader={stepHeader}
        options={options}
        defaultTradingPartnerPk={defaultTradingPartnerPk}
        onSubmit={onSubmit}
      />
    </Form>
  )
}

function ConnectHealthPlanStep({ isMember }: { isMember: boolean }): JSX.Element {
  const user = useCurrentUser()
  const { updateBenefitEnrollmentAnswers } = useUserDeductible()
  const intl = useIntl()

  const defaultTradingPartnerPk = isMember
    ? user.selectedTradingPartnerPk
    : user.partnerHealthPlanSelectedTradingPartnerPk
  const defaultHealthPlanId = isMember ? user.memberId : user.partnerHealthPlanMemberId

  const stepHeader = isMember
    ? intl.formatMessage({ defaultMessage: "Connect to your health insurance provider" })
    : intl.formatMessage({ defaultMessage: "Connect to their health insurance provider" })

  function onUserUpdate(healthPlanIdVal: string, tradingPartnerPkVal: string, tradingPartnerId: string): Promise<void> {
    return updateBenefitEnrollmentAnswers(
      isMember
        ? {
            selectedTradingPartnerId: tradingPartnerId,
            memberId: healthPlanIdVal,
            selectedTradingPartnerPk: tradingPartnerPkVal
          }
        : {
            partnerHealthPlanSelectedTradingPartnerId: tradingPartnerId,
            partnerHealthPlanMemberId: healthPlanIdVal,
            partnerHealthPlanSelectedTradingPartnerPk: tradingPartnerPkVal
          }
    )
  }

  return (
    <ProviderConnectYourInsurance
      defaultHealthPlanId={defaultHealthPlanId}
      onUserUpdate={onUserUpdate}
      defaultTradingPartnerPk={defaultTradingPartnerPk?.toString() || null}
      stepHeader={stepHeader}
      viewName={isMember ? Steps.MEMBER_HEALTH_PLAN_CONNECT : Steps.PARTNER_HEALTH_PLAN_CONNECT}
    />
  )
}

export function MemberConnectHealthPlanStep(): JSX.Element {
  return <ConnectHealthPlanStep isMember={true} />
}

export function PartnerConnectHealthPlanStep(): JSX.Element {
  return <ConnectHealthPlanStep isMember={false} />
}
